import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { getCloudinaryThumbnail } from '../lib/cloudinaryUtils';

export const ProductCard = ({ product, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);

  const discount = product.compare_price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : null;

  const isLowStock = product.total_stock > 0 && product.total_stock <= 5;
  const isOutOfStock = product.total_stock === 0;
  const images = product.images && product.images.length > 0 ? product.images : [];
  const showSecondImage = isHovered && images.length > 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: [0.25, 0.1, 0.25, 1] }}
      className="group"
      data-testid={`product-card-${product.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={`/producto/${product.slug}`}
        className="block"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-[#111] mb-3">
          {images.length > 0 ? (
            <>
              {/* Primary image */}
              <img
                src={getCloudinaryThumbnail(images[0])}
                alt={product.name}
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out ${
                  showSecondImage ? 'opacity-0 scale-105' : 'opacity-100 scale-100 group-hover:scale-[1.03]'
                }`}
                loading="lazy"
              />
              {/* Second image on hover */}
              {images.length > 1 && (
                <img
                  src={getCloudinaryThumbnail(images[1])}
                  alt={product.name}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out ${
                    showSecondImage ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                  }`}
                  loading="lazy"
                />
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/10">
              <ShoppingBag className="w-10 h-10" />
            </div>
          )}

          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Quick view text */}
          <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
            <span className="text-white text-[11px] font-medium tracking-wider uppercase">Ver producto</span>
          </div>

          {/* Badges */}
          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
            {discount && (
              <span className="bg-white text-black px-2 py-0.5 text-[10px] font-bold rounded-full backdrop-blur-sm">
                -{discount}%
              </span>
            )}
            {product.featured && !discount && (
              <span className="bg-gold text-black px-2 py-0.5 text-[10px] font-bold rounded-full">
                New
              </span>
            )}
          </div>

          {(isLowStock || isOutOfStock) && (
            <div className="absolute top-2.5 right-2.5">
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full backdrop-blur-sm ${
                isOutOfStock ? 'bg-black/60 text-white/70' : 'bg-black/60 text-orange-300'
              }`}>
                {isOutOfStock ? 'Agotado' : 'Últimas'}
              </span>
            </div>
          )}

          {/* Image count indicator */}
          {images.length > 1 && (
            <div className="absolute bottom-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="bg-black/50 backdrop-blur-sm text-white/70 text-[10px] px-1.5 py-0.5 rounded-full">
                1/{images.length}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-0.5 px-0.5">
          <h3 className="text-white text-[13px] md:text-sm font-medium group-hover:text-gold transition-colors duration-300 line-clamp-1">
            {product.name}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-white/80 font-semibold text-sm">
              ${product.price.toFixed(2)}
            </span>
            {product.compare_price && (
              <span className="text-white/25 line-through text-xs">
                ${product.compare_price.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
